import React, { useState } from 'react';
import { Edit2Icon, CheckIcon, XIcon, RefreshCwIcon, UserIcon, PhoneIcon } from 'lucide-react';
import type { MessageState } from '../../stores/bulkSendStore';

interface MessagePreviewCardProps {
  message: MessageState;
  onEdit: (clientId: string, editedText: string | null) => void;
  onRemove: (clientId: string) => void;
}

export function MessagePreviewCard({ message, onEdit, onRemove }: MessagePreviewCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message.editedText || message.originalText);

  const textToDisplay = message.editedText ?? message.originalText;
  const isEdited = message.editedText !== null;

  const handleStartEdit = () => {
    setEditText(textToDisplay);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (editText.trim() === message.originalText.trim()) {
      // No changes made, reset to original
      onEdit(message.clientId, null);
    } else {
      onEdit(message.clientId, editText.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditText(textToDisplay);
    setIsEditing(false);
  };

  const handleReset = () => {
    onEdit(message.clientId, null);
    setEditText(message.originalText);
  };

  const getStatusBadge = () => {
    switch (message.status) {
      case 'pending':
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">Pending</span>;
      case 'sending':
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
            <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
            Sending...
          </span>
        );
      case 'sent':
        return (
          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
            <CheckIcon className="w-3 h-3" />
            Sent
          </span>
        );
      case 'failed':
        return (
          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1">
            <XIcon className="w-3 h-3" />
            Failed
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`bg-white rounded-xl border-2 transition-all ${
        isEdited ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
      } ${message.status === 'failed' ? 'border-red-200 bg-red-50' : ''} ${
        message.status === 'sent' ? 'border-green-200 bg-green-50' : ''
      }`}
    >
      {/* Header with client info and status */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <UserIcon className="w-5 h-5 text-gray-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-gray-900 truncate">{message.clientName}</p>
            <p className="text-xs text-gray-500 flex items-center gap-1 truncate">
              <PhoneIcon className="w-3 h-3" />
              {message.jid.split('@')[0]}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge()}
          {message.status === 'pending' && (
            <button
              onClick={() => onRemove(message.clientId)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title="Remove from list"
            >
              <XIcon className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Message content */}
      <div className="p-4">
        {isEditing ? (
          <>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent text-sm resize-none"
              rows={6}
              placeholder="Edit message..."
            />
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleSaveEdit}
                disabled={!editText.trim()}
                className="flex-1 px-4 py-2 bg-[#5B8E87] text-white text-sm rounded-lg hover:bg-[#4F7A74] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckIcon className="w-4 h-4" />
                Save Changes
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-gray-50 rounded-lg p-3 mb-3">
              <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{textToDisplay}</p>
            </div>

            {/* Action buttons */}
            {message.status === 'pending' && (
              <div className="flex gap-2">
                <button
                  onClick={handleStartEdit}
                  className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <Edit2Icon className="w-4 h-4" />
                  Edit Message
                </button>
                {isEdited && (
                  <button
                    onClick={handleReset}
                    className="px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-lg hover:bg-blue-200 transition-colors flex items-center justify-center gap-2"
                    title="Reset to original"
                  >
                    <RefreshCwIcon className="w-4 h-4" />
                    Reset
                  </button>
                )}
              </div>
            )}

            {/* Error message */}
            {message.status === 'failed' && message.error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-700">
                  <span className="font-semibold">Error:</span> {message.error}
                </p>
              </div>
            )}

            {/* Edited indicator */}
            {isEdited && message.status === 'pending' && (
              <div className="mt-2 flex items-center gap-1 text-xs text-blue-600">
                <Edit2Icon className="w-3 h-3" />
                <span>This message has been edited</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
